import { FieldDef, ParseOptions } from './resolutionDetailsParser';

export type BackgroundSectionConfig = {
  id: string;
  match: RegExp;
  tableTitle: string;
  fields: FieldDef[];
  parseOptions?: ParseOptions;
  title: (values: Record<string, string>, name: string) => string;
  // Only sections whose wording was approved have these; the rest show the table alone.
  ask?: string;
};

const sentenceCase = (text: string) => {
  const lower = text.trim().toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const withDetail = (prefix: string, detail?: string) => (detail ? `${prefix} · ${detail}` : prefix);

export const BACKGROUND_SECTIONS: BackgroundSectionConfig[] = [
  {
    id: 'lienJudgment',
    match: /^Lien Judgment/i,
    tableTitle: 'Judgment on the background report',
    title: v => withDetail(v['Type'] ? sentenceCase(v['Type']) : 'Lien / judgment', v['Creditor']),
    fields: [
      { label: 'Type', keys: ['Filing Type'] },
      { label: 'Filed', keys: ['File Date'] },
      { label: 'Creditor', keys: ['Creditor', 'Creditor Name'] },
      { label: 'Amount', keys: ['Debtor Amount', 'Award Amount'], format: 'currency' },
      { label: 'Against', keys: ['Debtor Name'] },
      { label: 'Court', keys: ['Filing Office Name'] },
      { label: 'Filing number', keys: ['Filing Number'] },
      { label: 'Released', keys: ['Release Date'] },
      { label: 'Status date', keys: ['Status Date'] },
      { label: 'Type of action', keys: ['Type Of Action'], hideIfSameAs: 'Type' },
      { label: 'Unlawful detainer', keys: ['Unlawful Detainer'] },
      { label: 'Court code', keys: ['Court'] },
      // Internal ids that mean nothing to the borrower; internal users still see them.
      { label: 'Hidden filing number', keys: ['Hidden Filing Number'], sensitive: true },
      { label: 'DUNS number', keys: ['DUNS Number'], sensitive: true },
      { label: 'Filing office DUNS number', keys: ['Filing Office DUNS Number'], sensitive: true },
      { label: 'Debtor address', keys: ['Debtor Address'], format: 'address', joinWith: '\n' },
      { label: 'Creditor address', keys: ['Creditor Address'], format: 'address', joinWith: '\n' },
    ],
    ask: 'A civil judgment was filed against you or an entity you own. We need to know if it was paid, settled or is still open.',
  },
  {
    id: 'docket',
    match: /^Docket/i,
    tableTitle: 'Case on the background report',
    title: v => withDetail('Court case', v['Case type']),
    fields: [
      { label: 'Case title', keys: ['Case Title'] },
      { label: 'Case type', keys: ['Case Type/Subtype', 'Case Type'] },
      { label: 'Filed', keys: ['Filing Date', 'Filed Date'] },
      { label: 'Status', keys: ['Case Status'] },
      { label: 'Case number', keys: ['Case Number'] },
      { label: 'Court', keys: ['Court'] },
      { label: 'Nature of suit', keys: ['Key Nature of Suit'] },
      { label: 'Claim amount', keys: ['Claim/Demand Amount'], format: 'currency' },
      { label: 'Damages', keys: ['Damages'] },
      { label: 'Description', keys: ['Description'] },
      { label: 'Disposition', keys: ['Disposition'] },
      { label: 'Initiated as', keys: ['Case Initiation Type'], hideIfSameAs: 'Description' },
      { label: 'Dispute resolution', keys: ['Dispute Resolution'] },
      { label: 'Source', keys: ['Source'] },
    ],
    ask: 'You are listed as a party in a civil court case. Tell us your role in the case and whether it is closed.',
  },
  {
    id: 'otherNamesForSsn',
    match: /^Other Names For SSN/i,
    tableTitle: 'Name on the background report',
    title: () => 'Another name is linked to your SSN',
    fields: [
      { label: 'Name on record', keys: ['Name'] },
      { label: 'SSN', keys: ['SSN'], format: 'ssn' },
      { label: 'First seen', keys: ['First Seen'] },
      { label: 'Last seen', keys: ['Last Seen'] },
    ],
    ask: 'The report links a different name to your Social Security number. This is often a maiden name, a nickname or a typo. Tell us who it is.',
  },
  {
    id: 'otherSsns',
    match: /^Other SSNs?/i,
    tableTitle: 'SSN on the background report',
    title: () => 'Other SSN on the background report',
    fields: [
      { label: 'Name on record', keys: ['Name'] },
      { label: 'SSN', keys: ['SSN'], format: 'ssn' },
    ],
  },
  {
    id: 'ssnAddressFraud',
    match: /^S(NN|SN) Address/i,
    tableTitle: 'Alert on the background report',
    title: () => 'SSN and address alerts',
    fields: [
      { label: 'SSN', keys: ['SSN'], format: 'ssn' },
      { label: 'Address', keys: ['Address'], format: 'address' },
      { label: 'Alert', keys: ['Alert Indicator'] },
      { label: 'Description', keys: ['Alert Description'] },
      { label: 'SSN alert', keys: ['SSN Alert'], hidden: true },
      { label: 'SSN info', keys: ['SSN Info'], hidden: true },
      { label: 'Address alert', keys: ['Address Alert'], hidden: true },
      { label: 'Address info', keys: ['Address Info'], hidden: true },
    ],
  },
  {
    id: 'criminal',
    match: /^Criminal/i,
    tableTitle: 'Record on the background report',
    title: v => withDetail('Criminal record', v['Offense']),
    fields: [
      { label: 'Offense', keys: ['Criminal Offense'] },
      { label: 'Initial offense', keys: ['Initial Criminal Offense'], hideIfSameAs: 'Offense' },
      { label: 'Severity', keys: ['Severity of Crime', 'Class/Severity of Crime'] },
      { label: 'Case type', keys: ['Case Type'] },
      { label: 'Category', keys: ['Case General Category'], hideIfSameAs: 'Case type' },
      { label: 'Date of crime', keys: ['Date of Crime'] },
      { label: 'Arrest date', keys: ['Arrest Date'] },
      { label: 'Charges filed', keys: ['Charges Filing Date'] },
      { label: 'Case filed', keys: ['Case Filing', 'Case Filling'] },
      { label: 'Case status', keys: ['Case Status'] },
      { label: 'Disposition', keys: ['Disposition of Findings', 'Disposition of Crime', 'Disposition Category'] },
      { label: 'Disposition date', keys: ['Disposition Judgement Date', 'Disposition/Judgement Date'] },
      { label: 'Case number', keys: ['Case Number'] },
      { label: 'Case name', keys: ['Case Name'] },
      { label: 'Source', keys: ['Source Name'] },
      { label: 'Source state', keys: ['Source State'] },
      { label: 'Address', keys: ['Address'], format: 'address' },
      { label: 'Sequence number', keys: ['Sequence Number'] },
    ],
  },
  {
    id: 'arrest',
    match: /^Arrest/i,
    tableTitle: 'Record on the background report',
    title: v => withDetail('Arrest record', v['Offense']),
    fields: [
      { label: 'Name on record', keys: ['Offender Name'] },
      { label: 'Offense', keys: ['Offense'] },
      { label: 'Date of arrest', keys: ['Date of Arrest'] },
      { label: 'Date of birth', keys: ['DOB'], sensitive: true },
    ],
  },
  {
    id: 'bankruptcy',
    match: /^Bankruptcy/i,
    tableTitle: 'Filing on the background report',
    title: v => withDetail('Bankruptcy', v['Type']),
    fields: [
      { label: 'Type', keys: ['Filing Type'] },
      { label: 'Chapter', keys: ['Filing Chapter'] },
      { label: 'Filed', keys: ['Filing Date'] },
    ],
  },
  {
    id: 'foreclosure',
    match: /^Foreclosure/i,
    tableTitle: 'Filing on the background report',
    title: v => withDetail('Foreclosure', v['Type']),
    fields: [
      { label: 'Type', keys: ['Foreclosure'] },
      { label: 'Recorded', keys: ['Recording Date'] },
      { label: 'Property', keys: ['Situs Address'] },
      { label: 'Lis pendens type', keys: ['Lis Pendens Type'] },
      { label: 'Original loan amount', keys: ['Original Loan Amount'], format: 'currency' },
      { label: 'Auction date', keys: ['Auction Date'] },
    ],
  },
  {
    id: 'excludedParty',
    match: /^(Debarment|Exclusion)/i,
    tableTitle: 'Record on the background report',
    title: (v, name) => withDetail('Excluded party', v['Sanction type'] || name),
    parseOptions: { leadingUnlabeledAs: 'Name' },
    fields: [
      { label: 'Sanction type', keys: ['Sanction Type'] },
      { label: 'Entity type', keys: ['Entity Type'] },
      { label: 'Last reported', keys: ['Last Reported'] },
      { label: 'Published by', keys: ['Publication Name'] },
      { label: 'State', keys: ['State'] },
    ],
  },
  {
    id: 'globalSanctions',
    match: /^Global Sanction/i,
    tableTitle: 'Record on the background report',
    title: v => withDetail('Global sanction', v['Sanction issuer']),
    fields: [{ label: 'Sanction issuer', keys: ['Sanction Issuer'] }],
  },
  {
    id: 'lawsuit',
    match: /^Lawsuit/i,
    tableTitle: 'Lawsuit on the background report',
    title: v => withDetail('Lawsuit', v['Case type']),
    fields: [
      { label: 'Case type', keys: ['Case Type', 'File Type'] },
      { label: 'Filed', keys: ['Date Field', 'Filing Date', 'Date'] },
      { label: 'Plaintiff', keys: ['Plaintiff Name'] },
      { label: 'Against', keys: ['Defendant Name', 'Defendant'] },
      { label: 'Amount', keys: ['Amount', 'Demand'], format: 'currency' },
      { label: 'Case status', keys: ['Case Status'] },
      { label: 'Defendant status', keys: ['Defendant Status'] },
      { label: 'Status date', keys: ['Status Date'] },
      { label: 'Disposition code', keys: ['Disposition Code'] },
    ],
  },
];

// Removes the borrower id that prefixes unique_identifier ("<borrowerId> Docket Record ...").
export const stripBorrowerId = (itemName?: string) => (itemName || '').replace(/^\d+\s+/, '').trim();

export const findBackgroundSection = (name: string) => BACKGROUND_SECTIONS.find(section => section.match.test(name));
